import moment from 'moment';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { IoAdd, IoPencil } from 'react-icons/io5';
import Loader from '../../../core/components/Loader';
import { getApiStatus } from '../../../core/utils';
import { addTaskNote, updateTaskNote } from '../../tasks/actions';
import { Task } from '../../tasks/models';

interface NoteType {
  id: number;
  createdAt: string;
  text: string;
}

interface Props {
  task: Task;
}

const TaskDetailNotes = (props: Props): JSX.Element => {
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const dispatch = useDispatch();
  const noteQueryLoading = useSelector((state) => getApiStatus(state, 'GET_TASK'));
  const intl = useIntl();

  const notesList = props.task.notes ?? [];
  const notesSortedByRecent = [...(notesList as NoteType[])].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateB - dateA;
  });

  const handleAddNoteSubmit = () => {
    const notesInput = document.getElementById(
      'task-detail-notes-new-input'
    ) as HTMLTextAreaElement;
    if (!notesInput?.value?.trim()) return;
    dispatch(
      addTaskNote({
        id: props.task.id,
        text: notesInput.value.trim(),
      })
    );
    setAddNoteOpen(false);
    notesInput.value = '';
  };

  const handleStartEdit = (note: NoteType) => {
    setEditingNoteId(note.id);
    setEditingText(note.text);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingText('');
  };

  const handleSaveEdit = () => {
    if (editingNoteId == null || !props.task?.id) return;
    dispatch(
      updateTaskNote({
        taskId: props.task.id,
        noteId: editingNoteId,
        text: editingText,
      })
    );
    setEditingNoteId(null);
    setEditingText('');
  };

  return (
    <section className="task-detail-notes">
      <div className="task-detail-notes__header">
        <h3 className="task-detail-notes__title">{intl.formatMessage({ id: 'notes' })}</h3>
        <button
          type="button"
          className="button task-detail-notes__add-btn"
          onClick={() => setAddNoteOpen(!addNoteOpen)}
          aria-label={intl.formatMessage({ id: 'addNote' })}
        >
          <IoAdd size={20} />
          <span className="task-detail-notes__add-label">
            {intl.formatMessage({ id: 'addNote' })}
          </span>
        </button>
      </div>

      {addNoteOpen && (
        <div className="task-detail-notes__new-note">
          <textarea
            id="task-detail-notes-new-input"
            className="task-detail-notes__textarea"
            placeholder={intl.formatMessage({ id: 'addNote' }) + '...'}
            rows={3}
          />
          <div className="task-detail-notes__new-actions">
            <button type="button" className="button confirm mr-1" onClick={handleAddNoteSubmit}>
              {intl.formatMessage({ id: 'submit' })}
            </button>
            <button type="button" className="button" onClick={() => setAddNoteOpen(false)}>
              {intl.formatMessage({ id: 'cancel' })}
            </button>
          </div>
        </div>
      )}

      {noteQueryLoading ? (
        <Loader />
      ) : notesSortedByRecent.length > 0 ? (
        <ul className="task-detail-notes__list">
          {notesSortedByRecent.map((note) => (
            <li key={note.id} className="task-detail-notes__item">
              <span className="task-detail-notes__date">
                {moment(note.createdAt).format('MM/DD/YY')}
              </span>
              <div className="task-detail-notes__content">
                {editingNoteId === note.id ? (
                  <>
                    <textarea
                      className="task-detail-notes__textarea task-detail-notes__textarea--edit"
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      rows={3}
                    />
                    <div className="task-detail-notes__edit-actions">
                      <button
                        type="button"
                        className="button confirm mr-1"
                        onClick={handleSaveEdit}
                      >
                        {intl.formatMessage({ id: 'submit' })}
                      </button>
                      <button type="button" className="button" onClick={handleCancelEdit}>
                        {intl.formatMessage({ id: 'cancel' })}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="task-detail-notes__text">{note.text}</p>
                    <button
                      type="button"
                      className="task-detail-notes__edit-icon"
                      onClick={() => handleStartEdit(note)}
                      aria-label={intl.formatMessage({ id: 'edit' })}
                    >
                      <IoPencil size={18} color="#0d6efd" />
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        !addNoteOpen && (
          <p className="task-detail-notes__empty">{intl.formatMessage({ id: 'noNotes' })}</p>
        )
      )}
    </section>
  );
};

export default TaskDetailNotes;
